import type { ComponentPropsWithRef } from 'react';

import { Icon, type IconName } from '@/components/ui/icon';
import { Spinner } from '@/components/ui/spinner';
import { cn } from '@/lib/cn';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';

/**
 * Variant styles are plain lookup maps rather than a class-variance library -
 * one less dependency, and the full class list stays readable.
 *
 * The `chrome-*` classes carry the gradient, bevel and pressed-inset that give
 * the buttons their classic enterprise look; they are defined once in
 * styles/index.css.
 */
const VARIANTS: Record<ButtonVariant, string> = {
  primary: 'chrome-button-primary border text-primary-content',
  secondary: 'chrome-button border text-content',
  ghost:
    'border border-transparent bg-transparent text-content-muted hover:border-border hover:bg-surface-muted hover:text-content',
  danger: 'chrome-button-danger border text-white',
  link: 'border border-transparent bg-transparent text-primary underline-offset-2 hover:underline px-0',
};

/* Taller on phones for a usable touch target, dense from `sm` up. */
const SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 gap-1 px-2 text-2xs sm:h-6',
  md: 'h-9 gap-1.5 px-2.5 text-xs sm:h-7',
  lg: 'h-10 gap-1.5 px-3.5 text-xs sm:h-8',
};

const ICON_ONLY_SIZES: Record<ButtonSize, string> = {
  sm: 'h-8 w-8 p-0 sm:h-6 sm:w-6',
  md: 'h-9 w-9 p-0 sm:h-7 sm:w-7',
  lg: 'h-10 w-10 p-0 sm:h-8 sm:w-8',
};

export interface ButtonProps extends ComponentPropsWithRef<'button'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  /** Swaps the leading icon for a spinner and blocks interaction. */
  loading?: boolean;
  iconLeft?: IconName;
  iconRight?: IconName;
  /** Renders a square button. `aria-label` becomes required in practice. */
  iconOnly?: boolean;
  fullWidth?: boolean;
}

export function Button({
  variant = 'secondary',
  size = 'md',
  loading = false,
  iconLeft,
  iconRight,
  iconOnly = false,
  fullWidth = false,
  disabled,
  className,
  children,
  type = 'button',
  ...props
}: ButtonProps) {
  const iconSize = size === 'lg' ? 14 : 12;

  return (
    <button
      type={type}
      disabled={disabled ?? loading}
      aria-busy={loading ? true : undefined}
      className={cn(
        'inline-flex items-center justify-center rounded-sm font-medium whitespace-nowrap',
        'transition-colors duration-75',
        'disabled:pointer-events-none disabled:opacity-55',
        VARIANTS[variant],
        iconOnly ? ICON_ONLY_SIZES[size] : SIZES[size],
        fullWidth && 'w-full',
        className,
      )}
      {...props}
    >
      {loading ? <Spinner size={iconSize} /> : iconLeft && <Icon name={iconLeft} size={iconSize} />}
      {!iconOnly && children}
      {!loading && iconRight && <Icon name={iconRight} size={iconSize} />}
    </button>
  );
}
